/**
 * Invoice Service
 * Handles all invoice-related API calls
 * Supports both client-facing and admin operations
 */

import api from './api';
import { API_ENDPOINTS } from '../config/environment';

// Invoice status constants
export const INVOICE_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  SENT: 'sent',
  PAID: 'paid',
  OVERDUE: 'overdue',
  CANCELLED: 'cancelled',
};

// Invoice schedule types
export const INVOICE_SCHEDULE = {
  MONTHLY: 'monthly',
  WEEKLY: 'weekly',
  CUSTOM: 'custom',
};

const invoiceService = {
  /**
   * Get list of invoices for current client
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default 1)
   * @param {number} options.limit - Items per page (default 10)
   * @param {string} options.status - Filter by status
   * @returns {Promise<Object>} Invoice list with pagination
   */
  getInvoices: async ({ page = 1, limit = 10, status } = {}) => {
    const params = new URLSearchParams({ page, limit });
    if (status) params.append('status', status);

    const response = await api.get(`${API_ENDPOINTS.INVOICES}?${params}`);
    return response.data;
  },

  /**
   * Get download URL for an invoice PDF
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise<Object>} Download URL and expiry info
   */
  getInvoiceDownloadUrl: async (invoiceId) => {
    const response = await api.get(API_ENDPOINTS.INVOICE_DOWNLOAD(invoiceId));
    return response.data;
  },

  /**
   * Download invoice PDF directly
   * Opens the download URL in a new tab
   * @param {string} invoiceId - Invoice ID
   */
  downloadInvoice: async (invoiceId) => {
    const result = await invoiceService.getInvoiceDownloadUrl(invoiceId);
    if (result.download_url) {
      window.open(result.download_url, '_blank');
    } else {
      throw new Error('No download URL available');
    }
  },

  // =========================================================================
  // Admin API Methods (require admin privileges)
  // =========================================================================

  /**
   * Update invoice configuration for a client (admin only)
   * @param {string} clientId - Client identifier
   * @param {Object} config - Invoice configuration
   * @returns {Promise<Object>} Update result
   */
  adminUpdateInvoiceConfig: async (clientId, config) => {
    const response = await api.put(API_ENDPOINTS.ADMIN_INVOICE_CONFIG(clientId), config);
    return response.data;
  },

  /**
   * Get invoice configuration for a client (admin only)
   * @param {string} clientId - Client identifier
   * @returns {Promise<Object>} Current invoice configuration
   */
  adminGetInvoiceConfig: async (clientId) => {
    const response = await api.get(API_ENDPOINTS.ADMIN_BILLING_PLAN(clientId));
    return {
      invoice_email: response.data.invoice_email,
      invoice_cc: response.data.invoice_cc || [],
      invoice_schedule: response.data.invoice_schedule || 'monthly',
      invoice_day: response.data.invoice_day || 1,
      invoice_enabled: response.data.invoice_enabled !== false,
      invoice_language: response.data.invoice_language || 'en',
      company_name: response.data.company_name,
      company_address: response.data.company_address,
      payment_terms: response.data.payment_terms || 'net_30',
    };
  },

  /**
   * Generate invoice for a client (admin only)
   * @param {string} clientId - Client identifier
   * @param {string} month - Billing period in YYYY-MM format
   * @param {boolean} sendEmail - Whether to send email (default true)
   * @returns {Promise<Object>} Generated invoice details
   */
  adminGenerateInvoice: async (clientId, month, sendEmail = true) => {
    const response = await api.post(API_ENDPOINTS.ADMIN_GENERATE_INVOICE(clientId), {
      month,
      send_email: sendEmail,
    });
    return response.data;
  },

  /**
   * Preview invoice data without creating (admin only)
   * @param {string} clientId - Client identifier
   * @param {string} month - Billing period in YYYY-MM format
   * @returns {Promise<Object>} Invoice preview data
   */
  adminPreviewInvoice: async (clientId, month) => {
    const response = await api.get(
      `${API_ENDPOINTS.ADMIN_PREVIEW_INVOICE(clientId)}?month=${month}`
    );
    return response.data;
  },

  /**
   * Get all invoices across all clients (admin only)
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Invoice list with client info
   */
  adminGetAllInvoices: async ({ page = 1, limit = 20, status } = {}) => {
    const params = new URLSearchParams({ page, limit, all: 'true' });
    if (status) params.append('status', status);

    const response = await api.get(`${API_ENDPOINTS.INVOICES}?${params}`);
    return response.data;
  },
};

export default invoiceService;
